import {
  AnimationPropertiesOverride,
  BackgroundMaterial,
  Color3,
  Color4,
  CubeTexture,
  DirectionalLight,
  Engine,
  HemisphericLight,
  Mesh,
  MeshBuilder,
  Scene,
  SceneLoader,
  Texture,
  Vector2,
  Vector3,
} from "@babylonjs/core";
import { WaterMaterial } from "@babylonjs/materials";
import "@babylonjs/loaders";

import Player from "../player/Player";
import {
  ENVIRONMENT_CONFIG,
  ISLAND_CONFIG,
  LIGHTS_CONFIG,
  SCENE_CONFIG,
  SEA_CONFIG,
} from "../../config/game.config";

/**
 * BasicScene – creates and owns the Babylon Engine, Scene, lights, water,
 * foam particles, and the island/player assets.
 *
 * Scene-level constants (colours, intensities, asset URLs) are centralised
 * in src/config/game.config.ts.
 */
export class BasicScene {
  readonly scene: Scene;
  readonly engine: Engine;

  private waterMat?: WaterMaterial;

  constructor(private canvas: HTMLCanvasElement, private onReady?: () => void) {
    this.engine = new Engine(canvas, true);
    this.scene = this.createScene();

    this.scene.environmentIntensity = SCENE_CONFIG.environmentIntensity;
    this.setupDynamicSky();

    this.engine.runRenderLoop(() => {
      if (this.scene.activeCamera) {
        this.scene.render();
      }
    });

    window.addEventListener("resize", () => this.engine.resize());
  }

  private createScene(): Scene {
    const scene = new Scene(this.engine);

    // ── Collisions / gravity ─────────────────────────────────────────────────
    scene.collisionsEnabled = true;
    scene.gravity = new Vector3(
      SCENE_CONFIG.gravity.x,
      SCENE_CONFIG.gravity.y,
      SCENE_CONFIG.gravity.z
    );

    // ── Lights ───────────────────────────────────────────────────────────────
    this.setupLights(scene);

    // ── Sea ──────────────────────────────────────────────────────────────────
    this.waterMat = this.setupSea(scene);

    // ── Island & player (loaded sequentially so player spawns after island) ──
    this.loadIslandAndPlayer(scene);

    // ── Animation blending ───────────────────────────────────────────────────
    scene.animationPropertiesOverride = new AnimationPropertiesOverride();
    scene.animationPropertiesOverride.enableBlending = true;
    scene.animationPropertiesOverride.blendingSpeed = SCENE_CONFIG.animationBlendingSpeed;
    scene.animationPropertiesOverride.loopMode = 1;

    return scene;
  }

  // ── Lights ──────────────────────────────────────────────────────────────────

  private setupLights(scene: Scene): void {
    const { hemi, sun } = LIGHTS_CONFIG;

    const hemiLight = new HemisphericLight(
      "hemiLight",
      new Vector3(0, 1, 0),
      scene
    );
    hemiLight.intensity = hemi.intensity;
    hemiLight.diffuse = new Color3(hemi.diffuse.r, hemi.diffuse.g, hemi.diffuse.b);
    hemiLight.groundColor = new Color3(
      hemi.groundColor.r,
      hemi.groundColor.g,
      hemi.groundColor.b
    );

    const sunLight = new DirectionalLight(
      "sunLight",
      new Vector3(sun.direction.x, sun.direction.y, sun.direction.z),
      scene
    );
    sunLight.intensity = sun.intensity;
    sunLight.diffuse = new Color3(sun.diffuse.r, sun.diffuse.g, sun.diffuse.b);
    sunLight.specular = new Color3(sun.specular.r, sun.specular.g, sun.specular.b);
  }

  // ── Sea ──────────────────────────────────────────────────────────────────────

  private setupSea(scene: Scene): WaterMaterial {
    const cfg = SEA_CONFIG;

    const sea = MeshBuilder.CreateGround(
      "sea",
      { width: cfg.width, height: cfg.height, subdivisions: cfg.subdivisions },
      scene
    );
    sea.position.y = cfg.positionY;
    sea.metadata = { isSea: true };
    sea.checkCollisions = false;

    const waterMat = new WaterMaterial(
      "waterMat",
      scene,
      new Vector2(512, 512)
    );
    waterMat.bumpTexture = new Texture(cfg.bumpTextureUrl, scene);
    waterMat.bumpHeight = cfg.bumpHeight;
    waterMat.waveHeight = cfg.waveHeight;
    waterMat.waveLength = cfg.waveLength;
    waterMat.waveSpeed = cfg.waveSpeed;
    waterMat.windForce = cfg.windForce;
    waterMat.windDirection = new Vector2(
      cfg.windDirection.x,
      cfg.windDirection.y
    );
    waterMat.colorBlendFactor = cfg.colorBlendFactor;
    waterMat.waterColor = new Color3(
      cfg.waterColor.r,
      cfg.waterColor.g,
      cfg.waterColor.b
    );
    waterMat.waterColor2 = new Color3(
      cfg.waterColor2.r,
      cfg.waterColor2.g,
      cfg.waterColor2.b
    );
    sea.material = waterMat;

    return waterMat;
  }

  // ── Island & player ──────────────────────────────────────────────────────────

  private loadIslandAndPlayer(scene: Scene): void {
    const { modelFolder, modelFile, scaling } = ISLAND_CONFIG;

    SceneLoader.ImportMeshAsync("", modelFolder, modelFile, scene).then(
      async (result) => {
        result.meshes[0].scaling.set(scaling.x, scaling.y, scaling.z);

        for (const mesh of result.meshes) {
          mesh.checkCollisions = true;
          mesh.computeWorldMatrix(true);
          mesh.refreshBoundingInfo({});
        }

        // Player spawns after island geometry is ready to ensure
        // collision meshes are in place for the first frame.
        const player = new Player(scene);
        await player.createPlayerAndCamera();

        // Wait for every queued asset (player GLB, water bump, foam flare,
        // and the seven skybox HDR cube maps) to finish decoding/uploading
        // before hiding the loading screen.
        scene.executeWhenReady(() => this.onReady?.());
      }
    );
  }

  // ── Dynamic sky ──────────────────────────────────────────────────────────────

  /**
   * Crossfades between a list of HDR cube maps. Uses two skybox meshes:
   * - back: opaque, holds the currently visible texture
   * - front: alpha-fades 0→1 to reveal the next texture
   * After each fade, the next texture is moved onto the back mesh and the
   * front returns to alpha 0, ready for the cycle after.
   * IBL (scene.environmentTexture) snaps at the fade midpoint.
   */
  private setupDynamicSky(): void {
    const { files, scale, holdSeconds, fadeSeconds } = ENVIRONMENT_CONFIG;
    if (files.length === 0) return;

    // IBL textures (CUBIC_MODE — used by scene.environmentTexture for reflections).
    const iblTextures = files.map((f) =>
      CubeTexture.CreateFromPrefilteredData(f, this.scene)
    );
    // Skybox-mode clones (SKYBOX_MODE — required for the cube to stay world-aligned).
    const skyTextures = iblTextures.map((t) => {
      const clone = t.clone();
      clone.coordinatesMode = Texture.SKYBOX_MODE;
      return clone;
    });

    // Both skyboxes share renderingGroupId 0 so the depth buffer is preserved
    // between them. The opaque "back" renders in the opaque pass; the alpha-
    // blended "front" renders in the transparent pass (after opaque scene
    // geometry), and depth testing keeps it from drawing over the island/sea.
    const back = this.createSkyboxMesh("skyboxBack", scale, 0);
    const front = this.createSkyboxMesh("skyboxFront", scale, 0);
    // Force the alpha-blended front skybox to render before any other
    // transparent geometry (e.g. foliage leaves). With infiniteDistance the
    // skybox follows the camera so it would otherwise sort as "closest" and
    // cover trees during the fade.
    front.mesh.alphaIndex = -1;
    back.material.reflectionTexture = skyTextures[0];
    back.material.alpha = 1;
    front.material.alpha = 0;

    this.waterMat?.addToRenderList(back.mesh);
    this.waterMat?.addToRenderList(front.mesh);

    this.scene.environmentTexture = iblTextures[0];

    let nextIdx = files.length > 1 ? 1 : 0;
    let timer = 0;
    let fading = false;
    let iblSwapped = false;

    front.material.reflectionTexture = skyTextures[nextIdx];

    this.scene.onBeforeRenderObservable.add(() => {
      const dt = this.engine.getDeltaTime() / 1000;
      timer += dt;

      if (!fading) {
        if (files.length > 1 && timer >= holdSeconds) {
          fading = true;
          iblSwapped = false;
          timer = 0;
        }
        return;
      }

      const t = Math.min(timer / fadeSeconds, 1);
      front.material.alpha = t;

      if (!iblSwapped && t >= 0.5) {
        this.scene.environmentTexture = iblTextures[nextIdx];
        iblSwapped = true;
      }

      if (t >= 1) {
        // Fade complete: promote front's texture onto back, reset front to alpha 0,
        // and queue the next texture onto front for the upcoming cycle.
        back.material.reflectionTexture = skyTextures[nextIdx];
        front.material.alpha = 0;
        nextIdx = (nextIdx + 1) % files.length;
        front.material.reflectionTexture = skyTextures[nextIdx];
        fading = false;
        timer = 0;
      }
    });
  }

  private createSkyboxMesh(
    name: string,
    scale: number,
    renderingGroupId: number
  ): { mesh: Mesh; material: BackgroundMaterial } {
    const mesh = MeshBuilder.CreateBox(name, { size: scale }, this.scene);
    const material = new BackgroundMaterial(`${name}Mat`, this.scene);
    material.useRGBColor = false;
    material.backFaceCulling = false;
    mesh.material = material;
    mesh.infiniteDistance = true;
    mesh.checkCollisions = false;
    mesh.renderingGroupId = renderingGroupId;
    return { mesh, material };
  }
}
